import { isTourSearchResult, useTourTableData } from '../../onboarding/tableExercises';
import { getCurrentStep, useOnboardingStore } from '../../onboarding/onboardingStore';
import { TableComponent } from '../../ui/TableComponent';
import { useMemo, useState, useEffect, useLayoutEffect } from 'react';
import { TopSection, overallTypeLabel } from '../../ui/TopSection';
import { useRevenueHistory } from '../../../stores/appRevenueHistoryStore';
import { useAppUserDefaultStore, saveVatSetting, isAllRestaurantsSelected } from '../../../stores/appUserDefaultStore';
import { useFiltersStore } from '../../../stores/appFiltersStore';
import { filterRevenueByDateRange } from '../../../utils/dateRangeFilters';
import { calculateTopRevenueItems, aggregateRevenueItemsByCategory, calculatePercentageOfTotal } from '../../../utils/revenueHistoryCalculations';
import { calculateTotalRevenue } from '../../../utils/revenueHistoryCalculations';
import { filterRevenueByRestaurantSections, getAvailableCategories, getAvailableOverallTypeOptions, filterRevenueByOverallType, getAvailableMenuCategories, getAvailableRevenueSourceNames } from '../../revenuehistory/utils';
import { hasAnyChoices, aggregateAddOns } from '../../../utils/revenueAddOns';
import InfoHelper from '../../common/InfoHelper';
import { ChevronDown, ChevronUp, Package } from 'lucide-react';
import { metricIcon } from '../../ui/metricIcons';
import { eur } from '../../revenuehistory/utils';
import { cn } from '../../../lib/utils';
import MobilePageHeader from '../../shared/mobile/MobilePageHeader';
import RestaurantSelector from '../../common/RestaurantSelector';
import { ResponsiveTable, MobileCardList, MobileCardField } from '../../shared/mobile/ResponsiveTable';
import { filterAllRevenue } from '../../../utils/filterUtils';
import { useTimeControls } from '../../../hooks/useTimeControls';
import { useNavigate } from 'react-router-dom';
import { useLoadRevenueHistory, useLoadCostPrice } from '../../../utils/dataLoadingUtils';
import { StatCard } from '../../ui/StatCard';
import { useCostPriceStore } from '../../../stores/appCostPriceStore';
import { useCostPriceCategoriesStore } from '../../../stores/appCostPriceCategoriesStore';
import { createCostPriceAnalysis, updateCostPriceAnalysis } from '../../../services/costPrice/costPriceService';
import { createCostPriceCategory, updateCategoryPositions, CostPriceCategory } from '../../../services/costPrice/costPriceCategoriesService';
import { COST_PRICE_TYPE_CHOICE } from '../../../utils/costPriceUtils';
import CategoryAssignmentModal from '../../costPrice/CategoryAssignmentModal';
import { useRevenueFilters } from '../../../hooks/useRevenueFilters';
import { resetScrollToTop } from '../../../utils/scrollUtils';
import { useRestaurantStore } from '../../../stores/appRestaurantStore';
import { useTranslation } from 'react-i18next';
import { useFuzzySearch } from '../../../hooks/useFuzzySearch';
import RevenueItemQuantityEditModal from '../../revenuehistory/RevenueItemQuantityEditModal';
import { updateRevenueItemQuantity } from '../../../services/revenuehistory/revenueHistoryService';
import { useAuth } from '../../../services/authentication/authContext';
import { CategoryBadge } from '../../../utils/categoryHelper';
import { formatMainValue } from '../../ui/StatCard';

import { canAccessDemoAccount, canAccessCostPriceAutomation } from '../../../services/accessManagement/accessManagement';
import RevenueItemsExportMenu from './RevenueItemsExportMenu';
import {
  RevenueItemsExportContext,
  RevenueItemsExportRow,
  buildRevenueItemsClipboardText,
  buildRevenueItemsJson,
  buildRevenueItemsTxt,
  downloadTextFile,
  revenueItemsExportFilename,
} from './revenueItemsExport';
import {
  extraSmallTextSize,
  tertiaryText,
  mediumText,
  primaryText,
  smallTextSize,
  textBlue,
  semiBoldText,
  lightBlueBG,
  roundedLG,
  textDarkGreen,
  boldText,
  iconBgBlue,
} from '../../../styling/stylingHelper';


// How many add-ons to show before the "Show more" toggle in the expanded row.
const ADDONS_PREVIEW_COUNT = 5;

// Filter value standing for "no add-on category assigned" in the add-ons view's
// category column filter (mirrors the cost price BYO view's unassigned group).
const ADDON_UNASSIGNED_KEY = '__unassigned__';

/**
 * Inline panel rendered under an items-view row when it's expanded. Holds its
 * own "show all add-ons" state so it resets cleanly each time a row is opened.
 * Styling intentionally mirrors the parent table (white surface, table-family
 * typography, green-accented total) so the panel reads as part of the row, not
 * a popover. The top KPI strip carries the totals — the breakdown list below
 * focuses on the per-choice contribution.
 */
const ItemAddOnsExpandedPanel = ({ row }: { row: any }) => {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  const breakdown: { name: string; quantity: number; revenue: number }[] = row.addOnsBreakdown ?? [];
  const visible = showAll ? breakdown : breakdown.slice(0, ADDONS_PREVIEW_COUNT);
  const hiddenCount = Math.max(0, breakdown.length - ADDONS_PREVIEW_COUNT);

  const KpiTile = ({ label, value, accent }: { label: string; value: number; accent?: boolean }) => (
    <div className="flex-1 bg-white dark:bg-background-dark border border-[#E5E7EB] dark:border-borderPrimary-dark rounded-lg px-4 py-3">
      <div className="text-[12px] leading-4 font-medium text-[#475467] dark:text-textSecondary-dark mb-1">
        {label}
      </div>
      <div className={cn(
        "text-[18px] leading-6 font-semibold",
        accent ? "text-[#0E8965] dark:text-darkGreenColor-dark" : "text-[#101828] dark:text-textPrimary-dark"
      )}>
        {eur(value)}
      </div>
    </div>
  );

  return (
    <div className="bg-[#F9FAFB] dark:bg-backgroundDarker-dark px-6 py-4 border-t border-[#F2F4F7] dark:border-borderPrimary-dark space-y-4">
      {/* KPI strip — totals at a glance so the breakdown list can stay terse */}
      <div className="flex flex-col sm:flex-row gap-3">
        <KpiTile label={t('revenueHistory.addOns.baseRevenue')} value={row.baseRevenue ?? 0} />
        <KpiTile label={t('revenueHistory.addOns.addOnsRevenue')} value={row.addOnsRevenue ?? 0} />
        <KpiTile label={t('revenueHistory.addOns.totalRevenue')} value={row.totalPrice ?? 0} accent />
      </div>

      {/* Add-ons breakdown — same colgroup widths, header tokens, cell padding
          and value styling as the parent items table so this reads as a true
          sub-table of the dish row, not a popover. */}
      {breakdown.length > 0 && (
        <div className="bg-white dark:bg-background-dark border border-grey-200 dark:border-borderPrimary-dark rounded-lg overflow-hidden">
          <div className={cn(
            extraSmallTextSize,
            tertiaryText,
            mediumText,
            "px-3 py-2 border-b border-grey-200 dark:border-borderPrimary-dark uppercase tracking-wide"
          )}>
            {t('revenueHistory.addOns.topAddOns')}
          </div>
          <table className="w-full">
            <colgroup>
              <col style={{ width: '220px', minWidth: '120px', maxWidth: '300px' }} />
              <col style={{ width: '33%' }} />
              <col style={{ width: '33%' }} />
              <col style={{ width: '33%' }} />
            </colgroup>
            <thead>
              <tr className="bg-white dark:bg-background-dark border-b border-grey-200 dark:border-borderPrimary-dark">
                <th className={cn(extraSmallTextSize, tertiaryText, mediumText, "px-3 py-2 whitespace-nowrap text-left")}>
                  {t('revenueHistory.addOns.tableName')}
                </th>
                <th className={cn(extraSmallTextSize, tertiaryText, mediumText, "px-3 py-2 whitespace-nowrap text-center")}>
                  {t('revenueHistory.addOns.tableQuantity')}
                </th>
                <th className={cn(extraSmallTextSize, tertiaryText, mediumText, "px-3 py-2 whitespace-nowrap text-center")}>
                  {t('revenueHistory.addOns.tablePrice')}
                </th>
                <th className={cn(extraSmallTextSize, tertiaryText, mediumText, "px-3 py-2 whitespace-nowrap text-center")}>
                  {t('revenueHistory.addOns.tableRevenue')}
                </th>
              </tr>
            </thead>
            <tbody>
              {visible.map((c, i) => {
                // Per-pick price = revenue / quantity. Guards quantity === 0 so
                // a stray empty row doesn't render NaN.
                const unitPrice = c.quantity > 0 ? c.revenue / c.quantity : 0;
                return (
                <tr
                  key={c.name}
                  className={cn(
                    "py-2",
                    i < visible.length - 1 && "border-b border-grey-200 dark:border-borderPrimary-dark"
                  )}
                >
                  <td className="px-3 py-2 text-left">
                    <span className={cn(primaryText, smallTextSize, mediumText)}>+ {c.name}</span>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-center items-center w-full">
                      <span className={cn(textBlue, smallTextSize, semiBoldText, lightBlueBG, roundedLG, "px-2 py-1")}>
                        {c.quantity}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-center items-center w-full">
                      <span className={cn(primaryText, smallTextSize, mediumText)}>
                        {eur(unitPrice)}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-center items-center w-full">
                      <span className={cn(textDarkGreen, smallTextSize, boldText)}>
                        {eur(c.revenue)}
                      </span>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>

          {hiddenCount > 0 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowAll(!showAll);
              }}
              className={cn(
                smallTextSize,
                semiBoldText,
                textDarkGreen,
                "w-full px-3 py-2 border-t border-grey-200 dark:border-borderPrimary-dark hover:bg-[#EDFCF5] dark:hover:bg-[#EDFCF5]/10 transition-colors flex items-center justify-center gap-1"
              )}
            >
              {showAll ? t('revenueHistory.addOns.showLess') : t('revenueHistory.addOns.showMore', { count: hiddenCount })}
              {showAll ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// Rank badge gradient shared by the desktop table renders and the mobile cards.
const getItemRankStyle = (rank: number) => {
  if (rank === 1) return { background: 'linear-gradient(180deg, #0E8965 0%, #4AC4A0 100%)' };
  if (rank === 2) return { background: 'linear-gradient(180deg, #7F56D9 0%, #9F73FF 100%)' };
  if (rank === 3) return { background: 'linear-gradient(180deg, #F4B857 0%, #F48357 100%)' };
  return { background: 'linear-gradient(180deg, #8C97A6 0%, #818A97 100%)' };
};

// Elevated card surface for the mobile card list (matches the shipped RevenueHistoryPage cards).
const MOBILE_CARD =
  'bg-white dark:bg-card-dark rounded-xl p-4 shadow-[0px_4px_30px_0px_#4D546408] dark:shadow-[0px_4px_30px_0px_#00000040]';

// How many mobile cards to reveal per "Load more" tap (never render the whole list at once).
const MOBILE_BATCH = 20;

const RevenueHistoryItemsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { backendUser, accountSettings } = useAuth();

  // Reset scroll to top when this page loads
  useEffect(() => {
    resetScrollToTop();
  }, []);

  // Revenue history (the items table) loads progressively below, after the time controls
  // are known. Cost price has no time dimension here, so load it eagerly.
  useLoadCostPrice();

  // Connect to stores
  const revenueHistoryStore = useRevenueHistory();
  const showExVatRevenue = useAppUserDefaultStore(s => s.showExVat);
  const selectedRestaurant = useAppUserDefaultStore(s => s.restaurant);
  const restaurantId = selectedRestaurant?.id;

  // Cost price stores
  const costPriceStore = useCostPriceStore();
  const categoriesStore = useCostPriceCategoriesStore();

  // Restaurant store for filtering
  const restaurants = useRestaurantStore(s => s.restaurants);
  const restaurantRevenueSources = useRestaurantStore(s => s.restaurantRevenueSources);


  // Local state for modal
  const [editingItemCategory, setEditingItemCategory] = useState<{ itemName: string; displayName: string; currentCategoryId: string } | null>(null);

  // Local state for quantity editing modal
  const [editingQuantity, setEditingQuantity] = useState<{
    itemName: string;
    quantity: number;
    restaurantRevenueSourceId?: string;
  } | null>(null);

  // Local state for search
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Add-ons view only: multi-select column filter on the add-on category.
  // Holds category ids plus ADDON_UNASSIGNED_KEY for uncategorised choices.
  const [addOnCategoryFilters, setAddOnCategoryFilters] = useState<string[]>([]);

  // Local state for table pagination
  const [currentTablePage, setCurrentTablePage] = useState<number>(1);

  // Local state for Items / Category / Add-ons toggle.
  // 'addons' is only available when the period contains revenue items with choices.
  const [activeView, setActiveView] = useState<'items' | 'category' | 'addons'>('items');
  const tourStepId = useOnboardingStore(() => getCurrentStep()?.id);
  useEffect(() => {
    if (!tourStepId?.startsWith('revenue-')) return;
    // Back and replay must restore the view containing the next highlighted control.
    setActiveView(tourStepId === 'revenue-bigger-picture' ? 'category' : 'items');
  }, [tourStepId]);

  // Mobile-only: how many cards are currently rendered (grows on "Load more").
  // The desktop table paginates internally; on mobile we window the same array.
  const [mobileVisible, setMobileVisible] = useState<number>(MOBILE_BATCH);

  // Which item row (by product name) currently has its add-ons breakdown expanded.
  // Only one row open at a time; clicking again collapses it.
  const [expandedItemProduct, setExpandedItemProduct] = useState<string | null>(null);

  // Reset sort + collapse any open add-ons breakdown when switching views
  useEffect(() => {
    setSortColumn(null);
    setSortDirection(null);
    setExpandedItemProduct(null);
    setMobileVisible(MOBILE_BATCH);
  }, [activeView]);

  // Reset the mobile window when the search query changes so we never hide matches.
  useEffect(() => {
    setMobileVisible(MOBILE_BATCH);
  }, [searchQuery]);

  // Local state for sorting
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(null);

  useEffect(() => {
    console.log('currentTablePage', currentTablePage);
  }, [currentTablePage]);

  useEffect(() => {
    console.log('reloaded');
  }, []);

  const handleTablePageChange = (page: number) => {
    console.log('handleTablePageChange', page);
    setCurrentTablePage(page);
  };

  // Check if user is an admin who can edit quantities
  const isQuantityEditAdmin = canAccessDemoAccount(backendUser, accountSettings) && backendUser?.role === 'admin';

  // Exporting the items table rides on the cost price automation setting: the same
  // accounts (and impersonating super admins) that get automation get the exports.
  const canExportRevenueItems = canAccessCostPriceAutomation(backendUser, accountSettings);

  // Check if "All Restaurants" is selected
  const isAllRestaurants = selectedRestaurant && isAllRestaurantsSelected(selectedRestaurant);

  // The revenue sources in scope for this page: every restaurant's on "All Restaurants",
  // otherwise just the selected one's. Feeds both the overall-type options and the venue filter.
  const pageRevenueSources = useMemo(() => (
    isAllRestaurants
      ? Object.values(restaurantRevenueSources).flat()
      : (restaurantId ? restaurantRevenueSources[restaurantId] || [] : [])
  ), [isAllRestaurants, restaurantRevenueSources, restaurantId]);

  // Use centralized filter store
  const overallTypeFilter = useFiltersStore(s => s.overallTypeFilter);
  const setOverallTypeFilter = useFiltersStore(s => s.setOverallTypeFilter);
  const sectionFilters = useFiltersStore(s => s.sectionFilters);
  const restaurantFilters = useFiltersStore(s => s.restaurantFilters);
  const setRestaurantFilters = useFiltersStore(s => s.setRestaurantFilters);

  // Revenue filters hook - include all filters for this page
  const {
    filters,
    setRevenueCategoryFilters,
    setRevenueSourceFilters,
    setMenuCategoryFilters,
    resetFilters: resetRevenueFilters,
    activeFilterCount: baseActiveFilterCount,
  } = useRevenueFilters({
    includeRevenueCategories: true,
    includeRevenueSources: true,
    includeMenuCategories: true,
  });

  // Calculate total active filter count including restaurant filters
  const activeFilterCount = useMemo(() => {
    let count = baseActiveFilterCount;
    if (isAllRestaurants && restaurantFilters.length > 0) {
      count++;
    }
    return count;
  }, [baseActiveFilterCount, isAllRestaurants, restaurantFilters]);

  // Wrapper function to reset all filters including restaurant filters
  const resetFilters = () => {
    resetRevenueFilters();
    if (isAllRestaurants) {
      setRestaurantFilters([]);
    }
  };

  // Time controls - centralized logic. Declared before the data loading hook so the
  // progressive loader can prioritise the currently-viewed period.
  const {
    startDate,
    endDate,
    selectedTimeOption,
    dateRange,
    setDateRange,
    setSelectedTimeOption,
  } = useTimeControls();

  // Load revenue history progressively: the selected window (default last week) renders
  // first, then the rest of the history streams in the background.
  useLoadRevenueHistory({ progressive: true, viewStart: startDate });

  // Progressive-loading state: "loading" means the currently viewed period hasn't fully
  // streamed in yet. The all-time view can only be trusted once everything is loaded.
  const revenueFirstChunkLoaded = useRevenueHistory(s => s.firstChunkLoaded);
  const revenueFullyLoaded = useRevenueHistory(s => s.fullyLoaded);
  const revenueLoadedFromDate = useRevenueHistory(s => s.loadedFromDate);
  const isRevenueLoading =
    !revenueFullyLoaded && (
      !revenueFirstChunkLoaded ||
      selectedTimeOption === 'all' ||
      (!!revenueLoadedFromDate && startDate < revenueLoadedFromDate)
    );

  // Filter data by date range
  const dateFilteredRevenue = useMemo(() =>
    filterRevenueByDateRange(revenueHistoryStore.revenueHistoryList, startDate, endDate),
    [revenueHistoryStore.revenueHistoryList, startDate, endDate]
  );

  // Handler for overall type filter changes
  const handleOverallTypeChange = (value: string) => {
    setOverallTypeFilter(value as 'all' | 'food' | 'drinks' | 'take-away');
  };


  // Handler for VAT toggle - updates both orders and revenue VAT settings
  const handleVatChange = async (value: string) => {
    const showExVat = value === 'excl';
    try {
      // Update both settings in parallel
      await Promise.all([
        saveVatSetting(showExVat),
      ]);
    } catch (error) {
      console.error('Failed to save VAT settings:', error);
    }
  };

  // Apply overall type filter, then section filters, then revenue filters (revenue categories, menu categories)
  const overallTypeFilteredRevenue = useMemo(() =>
    filterRevenueByOverallType(dateFilteredRevenue, overallTypeFilter),
    [dateFilteredRevenue, overallTypeFilter]
  );

  const sectionFilteredRevenue = useMemo(() =>
    filterRevenueByRestaurantSections(overallTypeFilteredRevenue, sectionFilters),
    [overallTypeFilteredRevenue, sectionFilters]
  );

  const filteredRevenue = useMemo(() =>
    filterAllRevenue(
      sectionFilteredRevenue,
      filters,
      costPriceStore.costPriceList,
      categoriesStore.categories,
      isAllRestaurants ? restaurantFilters : undefined,
      pageRevenueSources
    ),
    [sectionFilteredRevenue, filters, costPriceStore.costPriceList, categoriesStore.categories, isAllRestaurants, restaurantFilters, pageRevenueSources]
  );

  // Calculate total revenue for percentage calculations (overall type + time filtered)
  const totalFilteredRevenue = useMemo(() =>
    calculateTotalRevenue(filteredRevenue, showExVatRevenue),
    [filteredRevenue, showExVatRevenue]
  );

  // Calculate top revenue items (show top 5 for the table with pagination)
  const topRevenueItems = useMemo(() =>
    calculateTopRevenueItems(
      filteredRevenue,
      showExVatRevenue,
      costPriceStore.costPriceList,
      categoriesStore.categories
    ).map((item, index) => ({
      ...item,
      rank: index + 1,
      percentageOfTotal: calculatePercentageOfTotal(item.totalPrice, totalFilteredRevenue),
    })),
    [filteredRevenue, showExVatRevenue, costPriceStore.costPriceList, categoriesStore.categories, totalFilteredRevenue]
  );

  // Calculate category aggregated data
  const categoryAggregatedData = useMemo(() =>
    aggregateRevenueItemsByCategory(
      filteredRevenue,
      showExVatRevenue,
      costPriceStore.costPriceList,
      categoriesStore.categories,
      totalFilteredRevenue
    ).map((item, index) => ({
      ...item,
      rank: index + 1,
    })),
    [filteredRevenue, showExVatRevenue, costPriceStore.costPriceList, categoriesStore.categories, totalFilteredRevenue]
  );

  // Add-ons (choices) — only surface the column/view when the period actually has any.
  const revenueHasAnyChoices = useMemo(() => hasAnyChoices(filteredRevenue), [filteredRevenue]);

  // Add-on categories (scope='addon'), in the same position order the cost price
  // BYO view groups its choices by.
  const addOnCategories = useMemo(() =>
    categoriesStore.categories
      .filter(c => (c.scope ?? 'revenue') === 'addon')
      .slice()
      .sort((a, b) => a.position - b.position),
    [categoriesStore.categories]
  );

  // choiceName -> add-on category, resolved exactly like the cost price BYO view:
  // the choice's CHOICE analysis carries the categoryId, and it only counts when it
  // points at an add-on-scoped category.
  const addOnCategoryByChoiceName = useMemo(() => {
    const byId = new Map(addOnCategories.map(c => [c.id, c]));
    const map = new Map<string, CostPriceCategory>();
    for (const analysis of costPriceStore.costPriceList) {
      if (analysis.costPriceType !== COST_PRICE_TYPE_CHOICE || analysis.restaurantId !== restaurantId) continue;
      const category = analysis.categoryId ? byId.get(analysis.categoryId) : undefined;
      if (category) map.set(analysis.costPriceName, category);
    }
    return map;
  }, [costPriceStore.costPriceList, addOnCategories, restaurantId]);

  // Aggregated add-ons across the period for the "Add-ons" view (merged by EXACT name,
  // sorted by revenue desc, includes €0 rows), each row carrying its add-on category.
  const addOnRows = useMemo(() =>
    aggregateAddOns(filteredRevenue, showExVatRevenue).map(row => {
      const category = addOnCategoryByChoiceName.get(row.name);
      return {
        ...row,
        addOnCategoryId: category?.id ?? ADDON_UNASSIGNED_KEY,
        addOnCategory: category?.name ?? '',
        addOnCategoryColor: category?.color,
      };
    }),
    [filteredRevenue, showExVatRevenue, addOnCategoryByChoiceName]
  );

  // Category column filter options — only categories actually present in the period,
  // plus "Unassigned" when there are uncategorised choices.
  const availableAddOnCategories = useMemo(() => {
    const present = new Set(addOnRows.map(r => r.addOnCategoryId));
    const options = addOnCategories
      .filter(c => present.has(c.id))
      .map(c => ({ id: c.id, name: c.name }));
    if (present.has(ADDON_UNASSIGNED_KEY)) {
      options.push({ id: ADDON_UNASSIGNED_KEY, name: t('common.unassigned') });
    }
    return options;
  }, [addOnRows, addOnCategories, t]);

  // Drop selections that no longer exist (restaurant switch, category deleted) so the
  // table can't silently render zero rows. Skipped while the options are still empty
  // (revenue is loading progressively) so a valid selection survives the first paint.
  useEffect(() => {
    if (addOnCategoryFilters.length === 0 || availableAddOnCategories.length === 0) return;
    const valid = new Set(availableAddOnCategories.map(o => o.id));
    const next = addOnCategoryFilters.filter(id => valid.has(id));
    if (next.length !== addOnCategoryFilters.length) setAddOnCategoryFilters(next);
  }, [availableAddOnCategories, addOnCategoryFilters]);

  // Apply the category filter, then rank — so ranks always read 1..n over what's on screen.
  const addOnsData = useMemo(() => {
    const rows = addOnCategoryFilters.length > 0
      ? addOnRows.filter(row => addOnCategoryFilters.includes(row.addOnCategoryId))
      : addOnRows;
    return rows.map((row, index) => ({ ...row, rank: index + 1 }));
  }, [addOnRows, addOnCategoryFilters]);

  // If the user is on the Add-ons view and choices disappear (filter / date change),
  // fall back to the Items view so we never render an empty/disabled toggle state.
  useEffect(() => {
    if (activeView === 'addons' && !revenueHasAnyChoices) {
      setActiveView('items');
    }
  }, [activeView, revenueHasAnyChoices]);

  // Apply fuzzy search query filter
  const searchFilteredItems = useFuzzySearch({
    data: topRevenueItems,
    // `displayName` too, so searching a partner venue ("bar pop") finds its rows.
    keys: ['product', 'displayName'],
    query: searchQuery,
    threshold: 0.4,
  });

  // Filter category data by search
  const searchFilteredCategories = useFuzzySearch({
    data: categoryAggregatedData,
    keys: ['categoryName'],
    query: searchQuery,
    threshold: 0.4,
  });

  // Filter add-ons by search
  const searchFilteredAddOns = useFuzzySearch({
    data: addOnsData,
    keys: ['name'],
    query: searchQuery,
    threshold: 0.4,
  });

  // Apply sorting to filtered data
  const sortedItems = useMemo(() => {
    if (!sortColumn || !sortDirection) return searchFilteredItems;
    
    return [...searchFilteredItems].sort((a, b) => {
      let aValue: number;
      let bValue: number;

      switch (sortColumn) {
        case 'items':
          aValue = a.items || 0;
          bValue = b.items || 0;
          break;
        case 'totalPrice':
          aValue = a.totalPrice || 0;
          bValue = b.totalPrice || 0;
          break;
        case 'price':
          aValue = a.price || 0;
          bValue = b.price || 0;
          break;
        case 'percentageOfTotal':
          aValue = a.percentageOfTotal || 0;
          bValue = b.percentageOfTotal || 0;
          break;
        default:
          return 0;
      }

      const comparison = aValue - bValue;
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [searchFilteredItems, sortColumn, sortDirection]);

  const sortedCategories = useMemo(() => {
    if (!sortColumn || !sortDirection) return searchFilteredCategories;

    return [...searchFilteredCategories].sort((a, b) => {
      let aValue: number;
      let bValue: number;

      switch (sortColumn) {
        case 'itemSold':
          aValue = a.itemSold || 0;
          bValue = b.itemSold || 0;
          break;
        case 'totalRevenue':
          aValue = a.totalRevenue || 0;
          bValue = b.totalRevenue || 0;
          break;
        case 'avgItemPrice':
          aValue = a.avgItemPrice || 0;
          bValue = b.avgItemPrice || 0;
          break;
        case 'percentageOfTotal':
          aValue = a.percentageOfTotal || 0;
          bValue = b.percentageOfTotal || 0;
          break;
        default:
          return 0;
      }

      const comparison = aValue - bValue;
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [searchFilteredCategories, sortColumn, sortDirection]);

  // Add-ons view sort (quantity / revenue).
  const sortedAddOns = useMemo(() => {
    if (!sortColumn || !sortDirection) return searchFilteredAddOns;
    return [...searchFilteredAddOns].sort((a, b) => {
      let aValue: number;
      let bValue: number;
      switch (sortColumn) {
        case 'quantity':
          aValue = a.quantity || 0;
          bValue = b.quantity || 0;
          break;
        case 'revenue':
          aValue = a.revenue || 0;
          bValue = b.revenue || 0;
          break;
        default:
          return 0;
      }
      const comparison = aValue - bValue;
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [searchFilteredAddOns, sortColumn, sortDirection]);

  // Determine which data to show based on active view
  const displayData = activeView === 'items'
    ? sortedItems
    : activeView === 'category'
      ? sortedCategories
      : sortedAddOns;

  // Recalculate KPIs based on filtered items
  const filteredTotalRevenue = useMemo(() => {
    if (activeView === 'items') {
      return sortedItems.reduce((acc: number, item: any) => acc + (item.totalPrice || 0), 0);
    } else if (activeView === 'category') {
      return sortedCategories.reduce((acc: number, item: any) => acc + (item.totalRevenue || 0), 0);
    } else {
      // Add-ons view: sum the choice revenue (this is choices-only, NOT total revenue).
      return sortedAddOns.reduce((acc: number, item: any) => acc + (item.revenue || 0), 0);
    }
  }, [activeView, sortedItems, sortedCategories, sortedAddOns]);

  const filteredItemsSold = useMemo(() => {
    if (activeView === 'items') {
      return sortedItems.reduce((acc: number, item: any) => acc + (item.items || 0), 0);
    } else if (activeView === 'category') {
      return sortedCategories.reduce((acc: number, item: any) => acc + (item.itemSold || 0), 0);
    } else {
      // Add-ons view: count of choice picks (proxy for "items sold" in this view).
      return sortedAddOns.reduce((acc: number, item: any) => acc + (item.quantity || 0), 0);
    }
  }, [activeView, sortedItems, sortedCategories, sortedAddOns]);

  // Handler for sorting
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortColumn(null);
        setSortDirection(null);
      }
    } else {
      // New column, start with ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleGoBack = () => {
    navigate("/revenuehistory")
  }

  // ---- Exports -------------------------------------------------------------
  // Every export takes `displayData` verbatim: the active view's rows with all filters,
  // the search query and the sort already applied. Pagination is display-only, so the
  // whole filtered set is exported, not just the page on screen.
  const exportRows = displayData as RevenueItemsExportRow[];

  // Built per export (not memoised) so `generatedAt` is the moment of the click.
  const buildExportContext = (): RevenueItemsExportContext => ({
    restaurantName: isAllRestaurants ? '' : selectedRestaurant?.name || '',
    periodLabel: `${startDate.toLocaleDateString()} – ${endDate.toLocaleDateString()}`,
    typeFilterLabel: overallTypeLabel(t, overallTypeFilter),
    view: activeView,
    showExVat: showExVatRevenue,
    searchQuery,
    activeFilterCount,
    // The table leaves the menu-category cell empty in the All Restaurants view.
    includeMenuCategory: !isAllRestaurants,
    totalRevenue: filteredTotalRevenue,
    totalItems: filteredItemsSold,
    generatedAt: new Date(),
  });

  const copyToClipboard = async (build: (ctx: RevenueItemsExportContext) => string): Promise<boolean> => {
    try {
      await navigator.clipboard.writeText(build(buildExportContext()));
      return true;
    } catch (error) {
      console.error('Failed to copy revenue items to clipboard:', error);
      return false;
    }
  };

  const handleCopyText = () => copyToClipboard(ctx => buildRevenueItemsClipboardText(t, exportRows, ctx));
  const handleCopyJson = () => copyToClipboard(ctx => buildRevenueItemsJson(t, exportRows, ctx));

  const handleDownloadJson = () => {
    const ctx = buildExportContext();
    downloadTextFile(buildRevenueItemsJson(t, exportRows, ctx), revenueItemsExportFilename(ctx, 'json'), 'application/json');
  };

  const handleDownloadTxt = () => {
    const ctx = buildExportContext();
    downloadTextFile(buildRevenueItemsTxt(t, exportRows, ctx), revenueItemsExportFilename(ctx, 'txt'), 'text/plain');
  };

  // exceljs (and the builder) is dynamically imported so it stays out of the main bundle.
  const handleDownloadExcel = async () => {
    const ctx = buildExportContext();
    try {
      const { exportRevenueItemsExcel } = await import('./revenueItemsExcelExport');
      await exportRevenueItemsExcel(t, exportRows, ctx);
    } catch (error) {
      console.error('Failed to export revenue items to Excel:', error);
    }
  };

  // Get available filter options from the section-filtered data (before applying revenue filters)
  const availableRevenueCategories = useMemo(() =>
    getAvailableCategories(sectionFilteredRevenue),
    [sectionFilteredRevenue]
  );

  // Get available menu categories based on overall type filter
  // When "all" is selected, show all categories; otherwise, only show categories present in filtered data
  const availableMenuCategories = useMemo(() => {
    if (overallTypeFilter === 'all') {
      // Show all categories when overall type is "all"
      return categoriesStore.categories.map(cat => ({
        id: cat.id,
        name: cat.name
      }));
    } else {
      // Only show categories that exist in the overall-type-filtered data
      return getAvailableMenuCategories(
        overallTypeFilteredRevenue,
        costPriceStore.costPriceList,
        categoriesStore.categories
      );
    }
  }, [overallTypeFilter, overallTypeFilteredRevenue, costPriceStore.costPriceList, categoriesStore.categories]);

  // Categories to show in the category assignment modal (filtered by overall type)
  const modalCategories = useMemo(() => {
    if (overallTypeFilter === 'all') return categoriesStore.categories;
    const availableIds = new Set(availableMenuCategories.map(c => c.id));
    return categoriesStore.categories.filter(cat =>
      cat.isDefault || availableIds.has(cat.id)
    );
  }, [overallTypeFilter, categoriesStore.categories, availableMenuCategories]);

  useLayoutEffect(() => {
    const search = (event: Event) => {
      setActiveView('items');
      setSearchQuery((event as CustomEvent<string>).detail);
    };
    window.addEventListener('onboarding:revenue-search', search);
    return () => window.removeEventListener('onboarding:revenue-search', search);
  }, []);

  useLayoutEffect(() => {
    const sort = (event: Event) => {
      const direction = (event as CustomEvent<'asc' | 'desc' | 'none'>).detail;
      setSearchQuery('');
      setActiveView('items');
      setSortColumn(direction === 'none' ? null : 'items');
      setSortDirection(direction === 'none' ? null : direction);
    };
    window.addEventListener('onboarding:revenue-sort', sort);
    return () => window.removeEventListener('onboarding:revenue-sort', sort);
  }, []);

  useTourTableData('revenue', { items: sortedItems.map(item => ({ id: item.displayName || item.product, name: item.displayName || item.product })), ready: !isRevenueLoading, categories: availableMenuCategories, search: searchQuery });

  useLayoutEffect(() => {
    const reset = () => {
      setActiveView('items');
      setSearchQuery('');
      setSortColumn(null);
      setSortDirection(null);
      setCurrentTablePage(1);
    };
    window.addEventListener('onboarding:revenue-reset-table', reset);
    return () => window.removeEventListener('onboarding:revenue-reset-table', reset);
  }, []);

  // Get available filter options from restaurants (fixed list from store, not from filtered data)
  const availableRestaurants = useMemo(() =>
    isAllRestaurants ? restaurants.map(r => ({ id: r.id, name: r.name })) : [],
    [isAllRestaurants, restaurants]
  );

  // Get available overall type options (base options + dynamic revenue source types)
  const availableOverallTypeOptions = useMemo(
    () => getAvailableOverallTypeOptions(pageRevenueSources),
    [pageRevenueSources]
  );

  // Venue options for the revenue source filter (distinct source names, e.g. "Untable" / "bar POP")
  const availableRevenueSources = useMemo(
    () => getAvailableRevenueSourceNames(pageRevenueSources),
    [pageRevenueSources]
  );

  // Reset filter to 'all' if current value is not available on this page
  useEffect(() => {
    if (overallTypeFilter && !availableOverallTypeOptions.includes(overallTypeFilter)) {
      setOverallTypeFilter('all');
    }
  }, [overallTypeFilter, availableOverallTypeOptions, setOverallTypeFilter]);

  // Drop venue selections this restaurant does not have (switching restaurant keeps the
  // store-level filter). Same idea as the overall-type reset above; skipped while the sources
  // are still loading, which would otherwise wipe a legitimate selection on first render.
  useEffect(() => {
    if (availableRevenueSources.length === 0 || filters.revenueSources.length === 0) return;
    const valid = filters.revenueSources.filter(name => availableRevenueSources.includes(name));
    if (valid.length !== filters.revenueSources.length) setRevenueSourceFilters?.(valid);
  }, [availableRevenueSources, filters.revenueSources, setRevenueSourceFilters]);

  // Handler functions for category editing
  const handleChangeItemCategory = async (itemName: string, newCategoryId: string) => {
    if (!restaurantId) return;

    // Find existing analysis or create new one
    const existingAnalysis = costPriceStore.costPriceList.find(
      analysis => analysis.menuItemName === itemName
    );

    try {
      if (existingAnalysis) {
        // Update existing analysis
        await updateCostPriceAnalysis({
          id: existingAnalysis.id,
          categoryId: newCategoryId
        });
      } else {
        // Create new analysis
        await createCostPriceAnalysis({
          restaurantId,
          costPriceName: itemName,
          menuItemName: itemName,
          categoryId: newCategoryId,
          ingredients: []
        });
      }

      await costPriceStore.load(restaurantId);
      setEditingItemCategory(null);
    } catch (error) {
      console.error('Failed to change item category:', error);
      alert('Failed to change item category');
    }
  };

  const handleCreateAndAssignCategory = async (categoryName: string) => {
    if (!restaurantId || !categoryName.trim() || !editingItemCategory) return;

    try {
      // Create new category (color will be auto-assigned by backend)
      const newCategory = await createCostPriceCategory({
        restaurantId,
        name: categoryName.trim()
      });

      // Reload categories
      await categoriesStore.reload();

      // Assign item to new category
      await handleChangeItemCategory(editingItemCategory.itemName, newCategory.id);
    } catch (error) {
      console.error('Failed to create and assign category:', error);
      alert('Failed to create and assign category');
    }
  };

  const handleReorderCategories = async (reorderedCategories: any[]) => {
    if (!restaurantId) return;

    try {
      const updates = reorderedCategories
        .filter(cat => !cat.isDefault)
        .map(cat => ({ id: cat.id, position: cat.position }));

      await updateCategoryPositions(updates);
      await categoriesStore.reload();
    } catch (error) {
      console.error('Failed to reorder categories:', error);
      alert('Failed to reorder categories');
    }
  };

  // Handler for quantity click - only available for admin users
  const handleQuantityClick = (item: any) => {
    if (!isQuantityEditAdmin) {
      return;
    }

    // The aggregated row already carries the source it came from. Falling back to "first entry
    // holding this name" would edit the WRONG source whenever a partner venue sells an item the
    // restaurant also sells itself under the same name.
    const restaurantRevenueSourceId = item.restaurantRevenueSourceId ?? dateFilteredRevenue.find(revenue => {
      return revenue.revenueData.categories.some(category =>
        category.items.some(i => i.name === item.product)
      );
    })?.restaurantRevenueSourceId;

    if (!restaurantRevenueSourceId) {
      console.log('Revenue entry not found for item:', item.product);
      return;
    }

    setEditingQuantity({
      itemName: item.product,
      quantity: item.items,
      restaurantRevenueSourceId
    });
  };

  // Handler for quantity update
  const handleUpdateQuantity = async (newQuantity: number) => {
    if (!editingQuantity || !editingQuantity.restaurantRevenueSourceId) {
      console.error('Missing restaurant revenue source information');
      throw new Error('Missing restaurant revenue source information');
    }

    try {
      const result = await updateRevenueItemQuantity(
        editingQuantity.restaurantRevenueSourceId,
        editingQuantity.itemName,
        newQuantity,
        startDate,
        endDate
      );

      // Force reload by clearing current revenue source IDs first
      if (restaurantId) {
        const revenueSources = restaurantRevenueSources[restaurantId] || [];
        const revenueSourceIds = revenueSources.map(rs => rs.id);

        // Clear the current revenue source IDs to force a fresh load
        useRevenueHistory.setState({ currentRevenueSourceIds: null });
        await revenueHistoryStore.loadByRevenueSourceIds(restaurantId, revenueSourceIds, restaurantRevenueSources);
      }

      console.log(`Successfully updated ${result.updateCount} revenue entries`);
    } catch (error) {
      console.error('Failed to update quantity:', error);
      throw error;
    }
  };

  return (
    <div data-onboarding="revenue-items-page">
      <MobilePageHeader title={t('revenueHistory.title')} right={<RestaurantSelector />} />
      <TopSection
          title={t('revenueHistory.title')}
          backButton={true}
          handleGoBack={handleGoBack}
          dateRange={dateRange}
          setDateRange={setDateRange}
          selectedTimeOption={selectedTimeOption}
          setSelectedTimeOption={setSelectedTimeOption}
          onOverallTypeChange={handleOverallTypeChange}
          currentOverallTypeValue={overallTypeFilter}
          overallTypeOptions={availableOverallTypeOptions}
          onVatChange={handleVatChange}
          currentVatValue={showExVatRevenue ? 'excl' : 'incl'}
          showFilters={true}
          rightControls={canExportRevenueItems ? (
            <RevenueItemsExportMenu
              onCopyText={handleCopyText}
              onCopyJson={handleCopyJson}
              onDownloadJson={handleDownloadJson}
              onDownloadTxt={handleDownloadTxt}
              onDownloadExcel={handleDownloadExcel}
            />
          ) : undefined}
          filters={{
            revenueFilters: {
              restaurantFilters: isAllRestaurants ? restaurantFilters : undefined,
              setRestaurantFilters: isAllRestaurants ? setRestaurantFilters : undefined,
              availableRestaurants: isAllRestaurants ? availableRestaurants : undefined,
              revenueCategoryFilters: filters.revenueCategories,
              setRevenueCategoryFilters,
              availableRevenueCategories,
              revenueSourceFilters: filters.revenueSources,
              setRevenueSourceFilters,
              availableRevenueSources,
              menuCategoryFilters: filters.menuCategories,
              setMenuCategoryFilters,
              availableMenuCategories,
              onResetFilters: resetFilters,
              activeFilterCount,
            }
          }}
        />

        {/* Responsive layout group for content sections below 1250px */}
      <div className="flex flex-col gap-6 min-[1250px]:flex-col">
        {/* KPI Cards, row on >=1250px, column below */}
        <div className="flex flex-col gap-3 sm:gap-6 min-[1250px]:flex-row">
          <div className="w-full min-[1250px]:flex-1">
            <StatCard
              {...metricIcon('revenue')}
              title={t('revenueHistory.totalRevenue')}
              value={filteredTotalRevenue}
              valueType="currency"
              loading={isRevenueLoading}
            />
          </div>
          <div className="w-full min-[1250px]:flex-1">
            <StatCard
              icon={<Package className="w-6 h-6" style={{ color: '#2E90FA' }} />}
              iconBg={iconBgBlue}
              title={t('revenueHistory.itemsSold')}
              value={filteredItemsSold}
              valueType="number"
              loading={isRevenueLoading}
            />
          </div>
        </div>

      {/* Responsive layout group for content sections below 1250px */}
      <div className="flex flex-col gap-6 min-[1250px]:flex-col">
        {/* top revenue items table component */}
        <div className="w-full" data-onboarding="revenue-items-table" data-onboarding-ready={!isRevenueLoading}>
          <ResponsiveTable
            desktop={
          <TableComponent
            title={t('revenueHistory.topRevenueItems')}
            data={displayData as any}
            blankLines={false}
            usePagination={true}
            renderExpandedRow={activeView === 'items' ? (row: any) => {
              // Only items view supports expansion. Show the panel only for the
              // currently-open row that actually has add-ons.
              // Row identity is the DISPLAY name: two rows can share `product` (the
              // restaurant's own item and a partner venue's), and only the display name separates them.
              if (expandedItemProduct !== row.displayName) return null;
              if ((row.addOnsBreakdown?.length ?? 0) === 0) return null;
              return <ItemAddOnsExpandedPanel row={row} />;
            } : undefined}
            onRowClick={activeView === 'items' ? (row: any) => {
              // Toggle the row's add-ons panel. `isRowClickable` below limits this
              // to rows that actually have a breakdown.
              setExpandedItemProduct(expandedItemProduct === row.displayName ? null : row.displayName);
            } : undefined}
            isRowClickable={activeView === 'items' ? (row: any) => {
              return (row.addOnsRevenue ?? 0) > 0 && (row.addOnsBreakdown?.length ?? 0) > 0;
            } : undefined}
            onQuantityClick={isQuantityEditAdmin && activeView === 'items' ? handleQuantityClick : undefined}
            initialPage={currentTablePage}
            onPageChange={handleTablePageChange}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder={t('revenueHistory.searchByProductName')}
            searchOnboardingId="revenue-items-search"
            rowOnboardingId={(row: any) => isTourSearchResult('revenue', row.displayName || row.product, searchQuery) ? "revenue-search-result" : undefined}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={handleSort}
            headerActions={
              <div className="flex shrink-0 bg-white dark:bg-background-dark border border-[#D0D5DD] dark:border-borderPrimary-dark rounded-lg h-10">
                <button
                  onClick={() => setActiveView('items')}
                  className={`px-4 py-1.5 text-[14px] leading-5 font-semibold whitespace-nowrap rounded-lg rounded-tr-none rounded-br-none transition-all ${
                    activeView === 'items'
                      ? 'bg-[#EDFCF5] dark:bg-[#EDFCF5]/20 text-[#0E8965] dark:text-[#0E8965]'
                      : 'text-[#344054] dark:text-textTertiary-dark hover:text-[#475467]'
                  }`}
                >
                  Items
                </button>
                <div className="h-[38px] bg-[#D0D5DD] dark:bg-borderPrimary-dark w-px"></div>
                <button
                  onClick={() => setActiveView('category')}
                  data-onboarding="revenue-view-category"
                  className={`px-4 py-1.5 text-[14px] leading-5 font-semibold whitespace-nowrap ${revenueHasAnyChoices ? 'rounded-none' : 'rounded-md rounded-tl-none rounded-bl-none'} transition-all ${
                    activeView === 'category'
                      ? 'bg-[#EDFCF5] dark:bg-[#EDFCF5]/20 text-[#0E8965] dark:text-[#0E8965]'
                      : 'text-[#344054] dark:text-textTertiary-dark hover:text-[#475467]'
                  }`}
                >
                  Category
                </button>
                {revenueHasAnyChoices && (
                  <>
                    <div className="h-[38px] bg-[#D0D5DD] dark:bg-borderPrimary-dark w-px"></div>
                    <button
                      onClick={() => setActiveView('addons')}
                      className={`px-4 py-1.5 text-[14px] leading-5 font-semibold whitespace-nowrap rounded-md rounded-tl-none rounded-bl-none transition-all ${
                        activeView === 'addons'
                          ? 'bg-[#EDFCF5] dark:bg-[#EDFCF5]/20 text-[#0E8965] dark:text-[#0E8965]'
                          : 'text-[#344054] dark:text-textTertiary-dark hover:text-[#475467]'
                      }`}
                    >
                      {t('revenueHistory.addOns.viewToggle')}
                    </button>
                  </>
                )}
              </div>
            }
            columns={(activeView === 'items' ? [
              { 
                key: 'product',
                onboardingColumnId: 'revenue-column-dish',
                label: t('revenueHistory.rankAndDishName'),
                columnType: 'name',
                // Renders `displayName`: a partner-venue row shows "<item> — <venue>" so it is
                // never confused with the restaurant's own identically-named item. The stored
                // name (`row.product`) is what every lookup below still keys off.
                render: (value: string, row: any) => {
                  const rank = row.rank || 1;
                  const getRankStyle = (rank: number) => {
                    if (rank === 1) return { background: 'linear-gradient(180deg, #0E8965 0%, #4AC4A0 100%)' };
                    if (rank === 2) return { background: 'linear-gradient(180deg, #7F56D9 0%, #9F73FF 100%)' };
                    if (rank === 3) return { background: 'linear-gradient(180deg, #F4B857 0%, #F48357 100%)' };
                    if (rank === 4) return { background: 'linear-gradient(180deg, #8C97A6 0%, #818A97 100%)' };
                    return { background: 'linear-gradient(180deg, #8C97A6 0%, #818A97 100%)' };
                  };
                  return (
                    <div className="flex items-center gap-2">
                      <div
                        className="w-5 h-5 rounded-full flex items-center justify-center text-white dark:text-white text-[11px] leading-[14px] tracking-[0.1px] font-bold flex-shrink-0"
                        style={getRankStyle(rank)}
                      >
                        {rank}
                      </div>
                      <span className="text-[#101828] dark:text-textPrimary-dark text-[14px] leading-5 font-medium">
                        {row.displayName || String(value)}
                      </span>
                    </div>
                  );
                }
              },
              {
                key: 'menuCategory',
                onboardingId: 'revenue-category-header', onboardingColumnId: 'revenue-column-category',
                label: t('revenueHistory.category'),
                ...((!isAllRestaurants) && {
                  filter: {
                    selectedValues: filters.menuCategories,
                    onValuesChange: setMenuCategoryFilters,
                    availableOptions: availableMenuCategories,
                    valueType: 'default',
                    filterLabel: 'Menu Categories',
                    maxHeight: 'max-h-[140px]',
                  }
                }),
                render: (value, row) => {
                  // Only show menu category if not "All Restaurants"
                  if (isAllRestaurants || !value) return null;

                  // Find the analysis and category to get the color
                  const currentAnalysis = costPriceStore.costPriceList.find(
                    analysis => analysis.menuItemName === row.product
                  );
                  const category = currentAnalysis?.categoryId
                    ? categoriesStore.byId(currentAnalysis.categoryId)
                    : null;
                  const categoryColor = category?.color;
                  const isDefaultCategory = category?.isDefault || false;

                  return (
                    <button
                      className="hover:opacity-80 transition-all"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setEditingItemCategory({
                          itemName: row.product,
                          // A partner venue's row differs from the restaurant's own by casing
                          // alone, so the modal has to name the row the way the table did.
                          displayName: row.displayName || row.product,
                          currentCategoryId: currentAnalysis?.categoryId || categoriesStore.getDefaultCategory()?.id || ''
                        });
                      }}
                    >
                      <CategoryBadge
                        categoryName={value}
                        categoryColor={categoryColor}
                        isDefaultCategory={isDefaultCategory}
                      />
                    </button>
                  );
                }
              },
              { key: 'items', label: t('revenueHistory.items'), columnType: 'blueQuantity', sortable: true, sortKey: 'items', sortOnHeaderClick: true, onboardingId: 'revenue-sort-headers revenue-quantity-sort', onboardingColumnId: 'revenue-column-quantity' },
              { key: 'price', label: t('revenueHistory.price'), columnType: 'currency', sortable: true, sortKey: 'price', onboardingId: 'revenue-sort-headers', onboardingColumnId: 'revenue-column-price' },
              {
                key: 'totalPrice',
                onboardingId: 'revenue-sort-headers', onboardingColumnId: 'revenue-column-total',
                // Header text + InfoHelper (only when there are any add-ons in the period,
                // since the helper exists specifically to explain the breakdown affordance).
                label: revenueHasAnyChoices ? (
                  <span className="flex items-center gap-1">
                    <InfoHelper
                      titleKey="onboardingHelper.addOns.title"
                      descriptionKey="onboardingHelper.addOns.description"
                    />
                    {t('revenueHistory.totalRevenue')}
                  </span>
                ) : t('revenueHistory.totalRevenue'),
                columnType: 'currencyHighlight',
                sortable: true,
                sortKey: 'totalPrice',
                // Custom render: when the item has add-ons, show value + chevron as a
                // visual indicator (the parent row is the actual click target via
                // `onRowClick` + `isRowClickable`). Pure currency text otherwise — matches
                // the non-BYO rows visually.
                render: (_value: any, row: any) => {
                  const hasAddOns = (row.addOnsRevenue ?? 0) > 0 && (row.addOnsBreakdown?.length ?? 0) > 0;
                  if (!hasAddOns) {
                    return <span className="text-[#0E8965] dark:text-darkGreenColor-dark font-semibold">{eur(row.totalPrice)}</span>;
                  }
                  const isOpen = expandedItemProduct === row.displayName;
                  return (
                    <span
                      aria-label={isOpen ? t('revenueHistory.addOns.hideBreakdown') : t('revenueHistory.addOns.showBreakdown')}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#EDFCF5] dark:bg-[#EDFCF5]/20 text-[#0E8965] dark:text-darkGreenColor-dark font-semibold"
                    >
                      {eur(row.totalPrice)}
                      <ChevronDown
                        size={14}
                        className={cn("transition-transform", isOpen && "rotate-180")}
                      />
                    </span>
                  );
                },
              },
              {
                key: 'percentageOfTotal',
                onboardingId: 'revenue-sort-headers',
                label: t('revenueHistory.percentOfTotal'),
                columnType: 'base',
                sortable: true,
                sortKey: 'percentageOfTotal',
                render: (value) => {
                  const percentage = value as number;
                  return (
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-[#F2F4F7] dark:bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#0E8965] dark:bg-[#0E8965] rounded-full"
                          style={{ width: `${Math.min(percentage, 100)}%` }}
                        />
                      </div>
                      <span className="text-[#0C111D] dark:text-textPrimary-dark text-[12px] leading-[18px] font-semibold">
                        {percentage.toFixed(1)}%
                      </span>
                    </div>
                  );
                }
              },
            ] : activeView === 'category' ? [
              {
                key: 'categoryName',
                label: t('revenueHistory.rankAndDishName'), 
                columnType: 'name',
                render: (value: string, row: any) => {
                  const rank = row.rank || 1;
                  const getRankStyle = (rank: number) => {
                    if (rank === 1) return { background: 'linear-gradient(180deg, #0E8965 0%, #4AC4A0 100%)' };
                    if (rank === 2) return { background: 'linear-gradient(180deg, #7F56D9 0%, #9F73FF 100%)' };
                    if (rank === 3) return { background: 'linear-gradient(180deg, #F4B857 0%, #F48357 100%)' };
                    if (rank === 4) return { background: 'linear-gradient(180deg, #8C97A6 0%, #818A97 100%)' };
                    return { background: 'linear-gradient(180deg, #8C97A6 0%, #818A97 100%)' };
                  };
                  return (
                    <div className="flex items-center gap-2">
                      <div
                        className="w-5 h-5 rounded-full flex items-center justify-center text-white dark:text-white text-[11px] leading-[14px] tracking-[0.1px] font-bold flex-shrink-0"
                        style={getRankStyle(rank)}
                      >
                        {rank}
                      </div>
                      <span className="text-[#101828] dark:text-textPrimary-dark text-[14px] leading-5 font-medium">
                        {String(value)}
                      </span>
                    </div>
                  );
                }
              },
              { key: 'itemSold', label: t('revenueHistory.items'), columnType: 'blueNumber', sortable: true, sortKey: 'itemSold' },
              {
                key: 'avgItemPrice',
                label: 'Average item Price',
                columnType: 'currency',
                sortable: true,
                sortKey: 'avgItemPrice',
                render: (value: any) => {
                  const price = value as number;
                  return formatMainValue(price, 'currency');
                }
              },
              { key: 'totalRevenue', label: t('revenueHistory.totalRevenue'), columnType: 'currencyHighlight', sortable: true, sortKey: 'totalRevenue' },
              {
                key: 'percentageOfTotal',
                label: t('revenueHistory.percentOfTotal'),
                columnType: 'base',
                sortable: true,
                sortKey: 'percentageOfTotal',
                render: (value: any) => {
                  const percentage = value as number;
                  return (
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-[#F2F4F7] dark:bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#0E8965] dark:bg-[#0E8965] rounded-full"
                          style={{ width: `${Math.min(percentage, 100)}%` }}
                        />
                      </div>
                      <span className="text-[#0C111D] dark:text-textPrimary-dark text-[12px] leading-[18px] font-semibold">
                        {percentage.toFixed(1)}%
                      </span>
                    </div>
                  );
                }
              },
            ] : [
              // Add-ons view — flat list of choices merged by exact name across the period.
              // Includes €0 rows on purpose so the user sees every choice the POS records.
              {
                key: 'name',
                label: t('revenueHistory.addOns.tableName'),
                columnType: 'name',
                render: (value: string, row: any) => {
                  const rank = row.rank || 1;
                  const getRankStyle = (rank: number) => {
                    if (rank === 1) return { background: 'linear-gradient(180deg, #0E8965 0%, #4AC4A0 100%)' };
                    if (rank === 2) return { background: 'linear-gradient(180deg, #7F56D9 0%, #9F73FF 100%)' };
                    if (rank === 3) return { background: 'linear-gradient(180deg, #F4B857 0%, #F48357 100%)' };
                    return { background: 'linear-gradient(180deg, #8C97A6 0%, #818A97 100%)' };
                  };
                  return (
                    <div className="flex items-center gap-2">
                      <div
                        className="w-5 h-5 rounded-full flex items-center justify-center text-white dark:text-white text-[11px] leading-[14px] tracking-[0.1px] font-bold flex-shrink-0"
                        style={getRankStyle(rank)}
                      >
                        {rank}
                      </div>
                      <span className="text-[#101828] dark:text-textPrimary-dark text-[14px] leading-5 font-medium">
                        {String(value)}
                      </span>
                    </div>
                  );
                },
              },
              {
                key: 'addOnCategory',
                label: t('revenueHistory.category'),
                // Add-on categories live on the restaurant's cost price data, which isn't
                // loaded in the All Restaurants view — same rule as the items view.
                ...((!isAllRestaurants) && {
                  filter: {
                    selectedValues: addOnCategoryFilters,
                    onValuesChange: setAddOnCategoryFilters,
                    availableOptions: availableAddOnCategories,
                    valueType: 'default',
                    filterLabel: t('revenueHistory.addOns.filterCategoryLabel'),
                    maxHeight: 'max-h-[140px]',
                  }
                }),
                render: (_value: any, row: any) => {
                  if (isAllRestaurants) return null;
                  if (!row.addOnCategory) {
                    return (
                      <span className="text-[#6B7280] dark:text-textTertiary-dark text-[14px] leading-5">
                        {t('common.unassigned')}
                      </span>
                    );
                  }
                  return (
                    <CategoryBadge
                      categoryName={row.addOnCategory}
                      categoryColor={row.addOnCategoryColor}
                    />
                  );
                },
              },
              { key: 'quantity', label: t('revenueHistory.addOns.tableQuantity'), columnType: 'blueQuantity', sortable: true, sortKey: 'quantity' },
              { key: 'revenue', label: t('revenueHistory.addOns.tableRevenue'), columnType: 'currencyHighlight', sortable: true, sortKey: 'revenue' },
            ] as any)}
          />
            }
            mobile={
              <div className="w-full">
                <h3 className={cn('mb-4 text-[18px] leading-6', semiBoldText, primaryText)}>{t('revenueHistory.topRevenueItems')}</h3>

                {/* View toggle (Items / Category / Add-ons) */}
                <div className="flex mb-3 h-10 overflow-hidden border rounded-lg bg-white dark:bg-background-dark border-[#D0D5DD] dark:border-borderPrimary-dark">
                  <button
                    type="button"
                    onClick={() => setActiveView('items')}
                    className={cn('flex-1 text-[14px] font-semibold transition-all', activeView === 'items' ? 'bg-[#EDFCF5] dark:bg-[#EDFCF5]/20 text-[#0E8965] dark:text-[#0E8965]' : 'text-[#344054] dark:text-textTertiary-dark')}
                  >
                    {t('revenueHistory.items')}
                  </button>
                  <div className="w-px bg-[#D0D5DD] dark:bg-borderPrimary-dark" />
                  <button
                    type="button"
                    onClick={() => setActiveView('category')}
                    className={cn('flex-1 text-[14px] font-semibold transition-all', activeView === 'category' ? 'bg-[#EDFCF5] dark:bg-[#EDFCF5]/20 text-[#0E8965] dark:text-[#0E8965]' : 'text-[#344054] dark:text-textTertiary-dark')}
                  >
                    {t('revenueHistory.category')}
                  </button>
                  {revenueHasAnyChoices && (
                    <>
                      <div className="w-px bg-[#D0D5DD] dark:bg-borderPrimary-dark" />
                      <button
                        type="button"
                        onClick={() => setActiveView('addons')}
                        className={cn('flex-1 text-[14px] font-semibold transition-all', activeView === 'addons' ? 'bg-[#EDFCF5] dark:bg-[#EDFCF5]/20 text-[#0E8965] dark:text-[#0E8965]' : 'text-[#344054] dark:text-textTertiary-dark')}
                      >
                        {t('revenueHistory.addOns.viewToggle')}
                      </button>
                    </>
                  )}
                </div>

                {/* Search */}
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('revenueHistory.searchByProductName')}
                  className={cn('w-full h-10 px-3 mb-3 border rounded-lg text-[14px] bg-white dark:bg-card-dark border-[#D0D5DD] dark:border-borderPrimary-dark', primaryText)}
                />

                <MobileCardList
                  items={(displayData as any[]).slice(0, mobileVisible)}
                  keyOf={(row: any, i) => `${activeView}-${row.rank ?? i}-${row.displayName ?? row.product ?? row.categoryName ?? row.name ?? ''}`}
                  onLoadMore={() => setMobileVisible((v) => v + MOBILE_BATCH)}
                  hasMore={mobileVisible < (displayData as any[]).length}
                  loadMoreLabel={t('revenueHistory.loadMore')}
                  renderCard={(row: any) => (
                    <div className={cn(MOBILE_CARD, 'flex flex-col gap-3')}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <div
                            className="w-5 h-5 rounded-full flex items-center justify-center text-white dark:text-white text-[11px] font-bold flex-shrink-0"
                            style={getItemRankStyle(row.rank || 1)}
                          >
                            {row.rank || 1}
                          </div>
                          <span className={cn('text-[14px] leading-5 font-medium truncate', primaryText)}>
                            {activeView === 'items' ? (row.displayName || row.product) : activeView === 'category' ? row.categoryName : row.name}
                          </span>
                        </div>
                        <span className={cn('text-base font-bold flex-shrink-0', textDarkGreen)}>
                          {eur(activeView === 'items' ? row.totalPrice : activeView === 'category' ? row.totalRevenue : row.revenue)}
                        </span>
                      </div>
                      <div className="flex items-stretch">
                        {activeView === 'addons' ? (
                          <>
                            {!isAllRestaurants && (
                              <MobileCardField
                                label={t('revenueHistory.category')}
                                value={row.addOnCategory || t('common.unassigned')}
                                divider
                              />
                            )}
                            <MobileCardField label={t('revenueHistory.addOns.tableQuantity')} value={String(row.quantity ?? 0)} />
                          </>
                        ) : activeView === 'category' ? (
                          <>
                            <MobileCardField label={t('revenueHistory.items')} value={String(row.itemSold ?? 0)} divider />
                            <MobileCardField label={t('revenueHistory.price')} value={eur(row.avgItemPrice ?? 0)} divider />
                            <MobileCardField label={t('revenueHistory.percentOfTotal')} value={`${(row.percentageOfTotal ?? 0).toFixed(1)}%`} />
                          </>
                        ) : (
                          <>
                            <MobileCardField label={t('revenueHistory.items')} value={String(row.items ?? 0)} divider />
                            <MobileCardField label={t('revenueHistory.price')} value={eur(row.price ?? 0)} divider />
                            <MobileCardField label={t('revenueHistory.percentOfTotal')} value={`${(row.percentageOfTotal ?? 0).toFixed(1)}%`} />
                          </>
                        )}
                      </div>
                    </div>
                  )}
                />
              </div>
            }
          />
        </div>
      </div>
    </div>

      {/* Category Assignment Modal */}
      {editingItemCategory && (
        <CategoryAssignmentModal
          itemName={editingItemCategory.itemName}
          displayName={editingItemCategory.displayName}
          currentCategoryId={editingItemCategory.currentCategoryId}
          categories={modalCategories}
          onSelectCategory={(categoryId) => handleChangeItemCategory(editingItemCategory.itemName, categoryId)}
          onCreateAndAssignCategory={handleCreateAndAssignCategory}
          onReorderCategories={handleReorderCategories}
          onCategoryUpdated={async () => {
            await categoriesStore.reload();
          }}
          onClose={() => setEditingItemCategory(null)}
        />
      )}

      {/* Quantity Edit Modal */}
      {editingQuantity && (
        <RevenueItemQuantityEditModal
          itemName={editingQuantity.itemName}
          currentQuantity={editingQuantity.quantity}
          onUpdateQuantity={handleUpdateQuantity}
          onClose={() => setEditingQuantity(null)}
        />
      )}
    </div>
  );
};

export default RevenueHistoryItemsPage;
